import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Home as HomeIcon, Award, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { usePageTitle } from '../components/PageTitle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Home: <HomeIcon className="w-8 h-8" />,
  Heart: <Heart className="w-8 h-8" />,
  Users: <Users className="w-8 h-8" />,
  Award: <Award className="w-8 h-8" />
};

const Home = () => {
  usePageTitle('Lehigh Human Support Services | LHSS');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default content (fallback if CMS is empty)
  const defaultContent = {
    hero: {
      title: 'Empowering Lives, Fostering Community',
      subtitle: 'Welcome to Lehigh Human Support Services, where compassionate care meets unparalleled expertise in supporting individuals with intellectual and developmental disabilities.',
      image: 'https://images.unsplash.com/photo-1732194438396-394d2b7c2436?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkaXNhYmlsaXR5JTIwc3VwcG9ydHxlbnwwfHx8fDE3Njg4MDY5NjF8MA&ixlib=rb-4.1.0&q=85',
      button1_text: 'Our Services',
      button1_link: '/services',
      button2_text: 'Get in Touch',
      button2_link: '/contact'
    },
    stats: [
      { number: '5+', label: 'Years of Service' },
      { number: '10+', label: 'Individuals Supported' },
      { number: '50+', label: 'Dedicated Staff' },
      { number: '24/7', label: 'Support Available' }
    ],
    services_title: 'Our Services',
    services_subtitle: 'Comprehensive support tailored to help individuals with intellectual and developmental disabilities thrive',
    services: [
      {
        icon: 'Home',
        title: 'Residential Support',
        description: 'Comprehensive residential services helping individuals live independently in their communities with dignity and support.',
        image: 'https://images.unsplash.com/photo-1723433892471-62f113c8c9a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkaXNhYmlsaXR5JTIwc3VwcG9ydHxlbnwwfHx8fDE3Njg4MDY5NjF8MA&ixlib=rb-4.1.0&q=85'
      },
      {
        icon: 'Heart',
        title: 'Personal Care',
        description: 'Compassionate one-on-one support for daily living activities, health management, and personal development.',
        image: 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwY2FyZWdpdmVyc3xlbnwwfHx8fDE3Njg4MDY5NjV8MA&ixlib=rb-4.1.0&q=85'
      },
      {
        icon: 'Users',
        title: 'Community Integration',
        description: 'Programs and activities that foster social connections, skill development, and meaningful community participation.',
        image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc2ODgwNjk3MXww&ixlib=rb-4.1.0&q=85'
      }
    ],
    mission_title: 'Our Mission',
    mission_text: 'At Lehigh Human Support Services, we are dedicated to empowering individuals with intellectual and developmental disabilities to live fulfilling, independent lives within their communities. Through compassionate care, professional expertise, and unwavering support, we help each person achieve their goals and reach their full potential.',
    mission_image: 'https://images.unsplash.com/photo-1631217868204-db1ed6bdd224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwY2FyZWdpdmVyc3xlbnwwfHx8fDE3Njg4MDY5NjV8MA&ixlib=rb-4.1.0&q=85',
    testimonials_title: 'What People Say',
    testimonials_subtitle: 'Hear from the families, individuals, and staff who experience our care firsthand',
    testimonials: [
      { quote: 'The staff at Lehigh HSS have been incredible. They truly care about my daughter and her growth.', author: 'Sarah M.', role: 'Parent' },
      { quote: 'I have found independence and purpose with the support of this amazing team.', author: 'Michael D.', role: 'Resident' },
      { quote: 'Working here has been the most rewarding experience of my career. We make a real difference.', author: 'Jennifer L.', role: 'Direct Support Professional' }
    ],
    cta_title: 'Join Our Team of Compassionate Caregivers',
    cta_subtitle: 'Make a meaningful difference in people\'s lives. Explore career opportunities with Lehigh HSS.',
    cta_button_text: 'View Open Positions',
    cta_button_link: '/careers'
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/home`);
      // Merge with defaults to ensure all fields exist
      const merged = { ...defaultContent, ...response.data };
      // Merge nested objects
      if (response.data.hero) {
        merged.hero = { ...defaultContent.hero, ...response.data.hero };
      }
      setContent(merged);
    } catch (error) {
      console.error('Error fetching home content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { hero, stats, services, testimonials } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {hero.title}
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                {hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to={hero.button1_link}>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                    {hero.button1_text} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to={hero.button2_link}>
                  <Button variant="outline" className="border-2 border-amber-500 text-amber-700 hover:bg-amber-50 px-8 py-6 text-lg transition-all duration-300">
                    {hero.button2_text}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={hero.image}
                alt="Caregiver with client"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-800 py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center space-y-2">
                <div className="text-4xl lg:text-5xl font-bold text-amber-400">{stat.number}</div>
                <div className="text-gray-300 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.services_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.services_subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border-2 border-gray-100">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-amber-500 text-white p-3 rounded-lg">
                    {iconMap[service.icon] || <HomeIcon className="w-8 h-8" />}
                  </div>
                </div>
                <CardContent className="p-6 space-y-3">
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  <Link to="/services" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-semibold transition-colors">
                    Learn More <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={content.mission_image}
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Award className="w-12 h-12 text-amber-500" />
                <h2 className="text-4xl font-bold text-gray-900">{content.mission_title}</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                {content.mission_text}
              </p>
              <Link to="/about">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg transition-all duration-300">
                  Learn About Us <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.testimonials_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.testimonials_subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-100 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 space-y-4">
                  <div className="text-5xl text-amber-400">"</div>
                  <p className="text-gray-700 leading-relaxed italic">{testimonial.quote}</p>
                  <div className="pt-4 border-t border-amber-200">
                    <div className="font-bold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            {content.cta_title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {content.cta_subtitle}
          </p>
          <Link to={content.cta_button_link}>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              {content.cta_button_text} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;